import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { EASE, Reveal } from "./Reveal";

const SLIDES = [
  {
    src: "/img/certificate-3m.png",
    alt: "3M India Limited Authorized Distributor Certificate — Universal Engineers",
    caption: "3M Authorized Distributor Certificate",
    tag: "Authorisation",
  },
  {
    src: "/img/memory-group.jpg",
    alt: "Universal Engineers team with the visiting 3M delegation from the USA",
    caption: "With the visiting 3M delegation from the USA",
    tag: "3M Delegation",
  },
  {
    src: "/img/memory-delegates.jpg",
    alt: "Welcoming 3M delegates from the USA with traditional garlands",
    caption: "Welcoming 3M delegates from the USA",
    tag: "3M Delegation",
  },
  {
    src: "/img/memory-thailand-group.jpg",
    alt: "Universal Engineers team at the 3M Dealers' Meet in Thailand, 2025",
    caption: "Dealers' Meet, Thailand — 2025",
    tag: "Dealers' Meet",
  },
  {
    src: "/img/memory-award.jpg",
    alt: "Receiving the sales performance award on stage at Elevate 2023",
    caption: "Elevate 2023 — sales award received on stage",
    tag: "Recognition",
  },
  {
    src: "/img/memory-booth.jpg",
    alt: "Universal Engineers team at the 3M booth during an electrical industry exhibition",
    caption: "At the 3M booth — electrical industry exhibition",
    tag: "Exhibitions",
  },
  {
    src: "/img/memory-thailand-trio.jpg",
    alt: "Team members at the 3M Dealers' Meet in Thailand, 2025",
    caption: "Team moments — Dealers' Meet, Thailand 2025",
    tag: "Dealers' Meet",
  },
  {
    src: "/img/memory-conversation.jpg",
    alt: "In conversation with the 3M delegation from the USA",
    caption: "In conversation with the 3M delegation from the USA",
    tag: "3M Delegation",
  },
  {
    src: "/img/memory-office-visit.jpg",
    alt: "3M team visit at the Universal Engineers Bhagirath Palace office",
    caption: "3M team visit — Bhagirath Palace office",
    tag: "Office Visits",
  },
];

const AUTOPLAY_MS = 4000;

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? "55%" : "-55%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-55%" : "55%", opacity: 0 }),
};

export const PhotoSlider = () => {
  const [[index, dir], setSlide] = useState([0, 1]);
  const touchX = useRef(null);
  const count = SLIDES.length;

  const go = (d) => setSlide(([i]) => [(i + d + count) % count, d]);
  const jump = (i) => setSlide(([cur]) => [i, i >= cur ? 1 : -1]);

  // Auto-rotate continuously
  useEffect(() => {
    const t = setInterval(() => setSlide(([i]) => [(i + 1) % count, 1]), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [count]);

  const slide = SLIDES[index];

  return (
    <section className="grain relative overflow-hidden bg-navy py-12 sm:py-16" data-testid="photo-slider-section">
      <div className="bp-grid absolute inset-0" />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
              <span className="inline-block h-2 w-2 bg-safety" /> Authorisation &amp; Moments
            </p>
            <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              Certified by 3M. Trusted for fifty years.
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="photo-slider-prev"
              aria-label="Previous photo"
              onClick={() => go(-1)}
              className="flex h-10 w-10 items-center justify-center border border-slate-600 text-white transition-colors hover:border-safety hover:text-safety"
            >
              <ArrowLeft size={16} />
            </button>
            <button
              type="button"
              data-testid="photo-slider-next"
              aria-label="Next photo"
              onClick={() => go(1)}
              className="flex h-10 w-10 items-center justify-center border border-slate-600 text-white transition-colors hover:border-safety hover:text-safety"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.06}>
          <div
            className="relative mx-auto mt-6 max-w-5xl select-none overflow-hidden border border-slate-700/70 bg-navy/60"
            data-testid="photo-slider"
            onTouchStart={(e) => {
              touchX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchX.current == null) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              if (Math.abs(dx) > 48) go(dx < 0 ? 1 : -1);
              touchX.current = null;
            }}
          >
            <div className="relative h-[220px] sm:h-[300px] lg:h-[360px]">
              <AnimatePresence initial={false} custom={dir}>
                <motion.img
                  key={slide.src}
                  src={slide.src}
                  alt={slide.alt}
                  data-testid="photo-slider-image"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.55, ease: EASE }}
                  draggable={false}
                  className="absolute inset-0 h-full w-full object-contain px-4 py-4 sm:px-6"
                />
              </AnimatePresence>
              <span
                className="absolute left-4 top-4 z-10 border border-white/20 bg-navy/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white backdrop-blur-sm"
                data-testid="photo-slider-tag"
              >
                {slide.tag}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-700/70 px-4 py-3 sm:px-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300" data-testid="photo-slider-caption">
                {slide.caption}
              </p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500" data-testid="photo-slider-counter">
                {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
              </p>
            </div>
          </div>

          <div className="mt-3.5 flex flex-wrap justify-center gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={s.src}
                type="button"
                data-testid={`photo-slider-dot-${i}`}
                aria-label={`Go to photo ${i + 1}`}
                onClick={() => jump(i)}
                className={`h-1.5 transition-all duration-300 ${i === index ? "w-8 bg-safety" : "w-3 bg-slate-600 hover:bg-slate-400"}`}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default PhotoSlider;
